<template>
  <!-- Token Form Dialog -->
  <create-token-confirmation
    :isHidden="createTokenDialogHidden"
    bodyInfoMessage=""
    titleInfoMessage="Create Token"
    buttonConfirmMessage="Create"
    variant="info"
    :okButtonEnabled="formEnabled"
    :large="true"
    @answerUser="createTokenDialogClosed"
    :show-cancel-button="true"
  >
    <div class="py-2">
      <div class="space-y-2">
        <label class="font-medium" for="form-elements-description"
          >Description</label
        >
        <input
          v-model="description"
          type="text"
          class="form-input"
          id="form-elements-description"
        />
      </div>
    </div>

    <div class="py-2">
      <div class="space-y-1">
        <label class="font-medium" for="form-elements-expiration"
          >Expiration</label
        >
        <select
          v-model="expiration"
          class="form-input"
          id="form-elements-description"
        >
          <option :value="{ value: 1, unit: 'w' }" key="1week" selected>
            1 week
          </option>
          <option :value="{ value: 1, unit: 'M' }" key="1months" selected>
            1 month
          </option>
          <option :value="{ value: 2, unit: 'M' }" key="2months" selected>
            2 months
          </option>
          <option :value="{ value: 3, unit: 'M' }" key="3months" selected>
            3 months
          </option>
          <option :value="{ value: 6, unit: 'M' }" key="6months" selected>
            6 months
          </option>
          <option :value="{ value: 999, unit: 'y' }" key="forever" selected>
            No expiration
          </option>
        </select>
      </div>
    </div>

    <div class="py-2">
      <div class="space-y-1">
        <h3 class="flex items-center my-4">
          <span
            aria-hidden="true"
            class="flex-grow bg-gray-200 rounded h-0.5"
          ></span>
          <span class="text-lg font-medium mx-3">Permissions</span>
          <span
            aria-hidden="true"
            class="flex-grow bg-gray-200 rounded h-0.5"
          ></span>
        </h3>
        <div class="mt-2 grid grid-cols-3">
          <label
            v-for="permission of userPermissions"
            :key="permission"
            class="inline-flex items-center mr-6 text-sm"
          >
            <input
              type="checkbox"
              :id="permission"
              :value="permission"
              v-model="selectedPermissions"
              class="border border-gray-200 rounded h-4 w-4 text-indigo-500 focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
            />
            <span class="ml-2">{{ permission }}</span>
          </label>
        </div>
        <br />
        <dm-button size="xs" @click="toggleAllPermissions()"
          >Toggle All</dm-button
        >
      </div>
    </div>

    <div class="py-2">
      <div class="space-y-2">
        <h3 class="flex items-center my-4">
          <span
            aria-hidden="true"
            class="flex-grow bg-gray-200 rounded h-0.5"
          ></span>
          <span class="text-lg font-medium mx-3">Namespaces</span>
          <span
            aria-hidden="true"
            class="flex-grow bg-gray-200 rounded h-0.5"
          ></span>
        </h3>
        <div class="mt-2">
          <div class="mt-2 grid grid-cols-2">
            <label
              v-for="namespace of userNamespaces"
              class="inline-flex items-center text-sm pt-1"
            >
              <input
                type="checkbox"
                :id="namespace.id"
                :value="namespace.id"
                v-model="selectedNamespaces"
                class="border border-gray-200 rounded h-4 w-4 text-indigo-500 focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
              />
              <span class="mx-2">{{ namespace.name }}</span>
            </label>
          </div>
          <br />
          <dm-button size="xs" @click="toggleAllNamespaces()"
            >Toggle All</dm-button
          >
        </div>
      </div>
    </div>
  </create-token-confirmation>
  <!-- End of Token Form Dialog -->

  <!-- Created Token Information-->
  <div
    v-if="newToken"
    class="rounded-xl text-emerald-700 bg-emerald-100 dark:text-emerald-100 dark:bg-emerald-900 dark:bg-opacity-75 p-4 space-y-4"
  >
    <div class="flex items-center rounded-xl">
      <svg
        class="hi-solid hi-check-circle inline-block w-5 h-5 mr-3 flex-none text-emerald-500 dark:text-emerald-400"
        fill="currentColor"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clip-rule="evenodd"
        />
      </svg>
      <h3 class="font-semibold">Token Created</h3>
    </div>
    <p>
      Make sure to copy your personal access token now. You won’t be able to see
      it again!
    </p>
    <div class="bg-gray-100 rounded-md py-2 px-4 flex flex-row justify-between">
      <pre class="align-bottom">{{ newToken }}</pre>
      <button-copy-clipboard :dataToCopy="newToken" />
    </div>
  </div>
  <!-- End of Created Token Information-->

  <h2 class="page-title">Tokens</h2>
  <div class="flex flex-row justify-end mb-4">
    <DmButton @click.prevent="createNewTokenClicked">Create New Token</DmButton>
  </div>

  <ErrorDisplay
    v-if="errorMessage != ''"
    class="mb-4"
    headerMessage="Could not create token"
    :error="errorMessage"
  />

  <h3 class="my-4 text-xl font-bold">Your User Tokens</h3>

  <Table
    v-if="result?.viewer"
    center-header
    :headers="['Description', 'Expires At', 'Actions']"
    :hasNextPage="!!result?.viewer.tokens.pageInfo.hasNextPage"
    :hasPreviousPage="!!result?.viewer.tokens.pageInfo.hasPreviousPage"
    @changePage="(e) => (e === 'next' ? loadNext() : loadPrevious())"
  >
    <tr
      v-for="tokenEdge of result.viewer.tokens.edges"
      :key="tokenEdge.node.id"
      class="border-b border-gray-200 text-center even:bg-gray-50"
    >
      <td class="p-3 text-center">
        {{ tokenEdge.node.description }}
      </td>
      <td>
        {{ tokenEdge.node.expiresAt }}
      </td>
      <td>
        <DmButton
          variant="danger"
          size="xs"
          @click="deleteTokenClicked(tokenEdge.node.id)"
          >Delete</DmButton
        >
      </td>
    </tr>
  </Table>
</template>

<script setup lang="ts">
import { useMutation, useQuery } from '@vue/apollo-composable';
import dayjs, { ManipulateType, OpUnitType } from 'dayjs';
import { Ref, computed, ref } from 'vue';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/vue/outline';

import { graphql } from '../../__generated__/gql';

import { getAllNamespaces } from '../namespaces/allNamespaces';

import CreateTokenConfirmation from '../base/DialogMessage.vue';
import DmButton from '@/components/base/Button.vue';
import ButtonCopyClipboard from '@/components/base/ButtonCopyClipboard.vue';
import { getUserPermissions, getUserNamespaces } from '../../auth';
import Table from '../base/Table.vue';
import SelectUser from '../users/SelectUser.vue';
import ErrorDisplay from '../ErrorDisplay.vue';

interface ViewerQueryVariables {
  first?: number;
  after?: string;
  last?: number;
  before?: string;
}

const numberOfEntriesToDisplay = 100;

const viewerQuery = graphql(`
  query Viewer($first: Int, $after: String, $last: Int, $before: String) {
    viewer {
      id
      tokens(first: $first, after: $after, last: $last, before: $before) {
        pageInfo {
          hasPreviousPage
          hasNextPage
          endCursor
          startCursor
        }
        edges {
          node {
            id
            description
            expiresAt
          }
        }
      }
    }
  }
`);

const deleteTokenMutation = graphql(`
  mutation DeleteUserToken($id: ID!) {
    deleteUserToken(id: $id) {
      id
      success
    }
  }
`);

const createTokenMutation = graphql(`
  mutation CreateUserToken(
    $description: String!
    $expiresAt: DateTime
    $permissions: [Permission!]!
    $namespaceIds: [ID!]!
    $userId: ID
  ) {
    createUserToken(
      input: {
        description: $description
        expiresAt: $expiresAt
        permissions: $permissions
        namespaceIds: $namespaceIds
        userId: $userId
      }
    ) {
      token
    }
  }
`);

// A flag to indicate if the 'create token' should be displayed.
const createTokenDialogHidden = ref(true);
const errorMessage = ref('');

// Getting the user permissions and namespaces from the identity token, to populate the form.
const userPermissions = computed(() => (getUserPermissions() ?? []).sort());
const userNamespaces = computed(() => (getUserNamespaces() ?? []).sort());

// Form content.
const selectedPermissions: Ref<string[]> = ref([]);
const selectedNamespaces: Ref<string[]> = ref([]);
const description = ref('');

// Un/Select all permissions and namespaces
const toggleAllPermissions = () => {
  if (selectedPermissions.value.length === userPermissions.value.length) {
    selectedPermissions.value = [];
  } else {
    selectedPermissions.value = userPermissions.value;
  }
};
const toggleAllNamespaces = () => {
  if (selectedNamespaces.value.length === userNamespaces.value.length) {
    selectedNamespaces.value = [];
  } else {
    selectedNamespaces.value = userNamespaces.value.map(
      (namespace) => namespace.id
    );
  }
};

// The lifetime of the token, as a value/unit pair, where 'unit'
// is one of those defined by dayjs
// https://day.js.org/docs/en/display/difference#list-of-all-available-units.
const expiration = ref({ value: 1, unit: 'w' });

// The newly created token.
const newToken: Ref<string | undefined> = ref();

// Whether the form contains the minimum information to create a token.
const formEnabled = computed(() => {
  return (
    description.value.trim() != '' &&
    selectedPermissions.value.length > 0 &&
    selectedNamespaces.value.length > 0
  );
});

// Tokens pagination variables.
const tokensVariables: Ref<ViewerQueryVariables> = ref({
  first: numberOfEntriesToDisplay,
  last: undefined,
  before: undefined,
  after: undefined,
});

// Tokens list query.
const {
  result,
  fetchMore,
  refetch: refetchViewer,
} = useQuery(viewerQuery, tokensVariables);

// Resets all the field in the form, this must be called whenever the form is to be displayed
// for a new token creation.
function resetForm() {
  selectedPermissions.value = [];
  selectedNamespaces.value = [];
  description.value = '';
  expiration.value = { value: 1, unit: 'w' };
}

// The mutation that creates a new token.
const {
  mutate: createUserToken,
  onDone: createUserTokenDone,
  onError: createUserTokenError,
} = useMutation(createTokenMutation, () => ({
  variables: {
    description: description.value,
    expiresAt: dayjs()
      .add(expiration.value.value, expiration.value.unit as ManipulateType)
      .toISOString(),
    permissions: selectedPermissions.value,
    namespaceIds: selectedNamespaces.value,
    userId: result?.value?.viewer.id,
  },
}));

// The mutation that deletes a token.
const { mutate: deleteUserToken, onDone: deleteUserTokenDone } =
  useMutation(deleteTokenMutation);

createUserTokenDone((result) => {
  errorMessage.value = '';
  newToken.value = result?.data?.createUserToken.token ?? undefined;
  refetchViewer();
});

deleteUserTokenDone(() => {
  refetchViewer();
});

createUserTokenError((error) => {
  errorMessage.value = error.message;
});

const createTokenDialogClosed = async (answer: boolean) => {
  createTokenDialogHidden.value = true;
  if (answer) {
    createUserToken();
  }
};

// Move pagination forward.
function loadNext() {
  fetchMore({
    variables: {
      first: numberOfEntriesToDisplay,
      last: undefined,
      after: result?.value?.viewer.tokens.pageInfo.endCursor ?? undefined,
    },
    updateQuery: (nextResult, { fetchMoreResult }) =>
      fetchMoreResult || nextResult,
  });
}

// Move pagination backward.
function loadPrevious() {
  fetchMore({
    variables: {
      last: numberOfEntriesToDisplay,
      first: undefined,
      before: result?.value?.viewer.tokens.pageInfo.startCursor ?? undefined,
    },
    updateQuery: (previousResult, { fetchMoreResult }) =>
      fetchMoreResult || previousResult,
  });
}

function deleteTokenClicked(tokenId: string) {
  deleteUserToken({ id: tokenId });
}

function createNewTokenClicked() {
  resetForm();
  createTokenDialogHidden.value = false;
}
</script>

<style>
.form-input {
  @apply w-full block border border-gray-200 rounded px-3 py-2 leading-6 focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50;
}
</style>
